import moment from 'moment';

export function secondsToHours(seconds, decimals = 1) {
  if (!seconds) return '0';
  const hours = seconds / 3600;
  return hours.toFixed(decimals);
}

export function absenceSeconds(uo) {
  return (
    (uo.payedVacationInSeconds || 0) +
    (uo.unpayedVacationInSeconds || 0) +
    (uo.sickTimeInSeconds || 0)
  );
}

export function effectivityPercent(uo) {
  const worked = uo.workedTimeInSeconds || 0;
  if (!worked) return null;
  return Math.round(((uo.customerWorkedTimeInSeconds || 0) / worked) * 100);
}

export function effectivityWithAbsencePercent(uo) {
  const denominator = (uo.workedTimeInSeconds || 0) + absenceSeconds(uo);
  if (!denominator) return null;
  return Math.round(
    ((uo.customerWorkedTimeInSeconds || 0) / denominator) * 100,
  );
}

export function targetAchievementPercent(uo) {
  const target = uo.targetWorkedTimeInSeconds || 0;
  const absenseSeconds = absenceSeconds(uo);
  if (!target) return null;
  return Math.round(
    ((uo.workedTimeInSeconds || 0) / (target - absenseSeconds)) * 100,
  );
}

export function absenceRatePercent(uo) {
  const absence = absenceSeconds(uo);
  const denominator = (uo.workedTimeInSeconds || 0) + absence;
  if (!denominator) return null;
  return Math.round((absence / denominator) * 100);
}

export function projectSharePercent(p) {
  const total = p.totalWorkedTimeInSeconds || 0;
  if (!total) return null;
  return Math.round(((p.workedTimeInSeconds || 0) / total) * 100);
}

export function workPackageSharePercent(wp) {
  const total = wp.totalWorkedTimeInSeconds || 0;
  if (!total) return null;
  return Math.round(((wp.userWorkedTimeInSeconds || 0) / total) * 100);
}

export function packageProgressPercent(wp) {
  const estimated = wp.workPackage?.estimatedTimeSeconds;
  if (!estimated) return null;
  return Math.round(
    ((wp.workPackage.totalTimeWorkedSeconds || 0) / estimated) * 100,
  );
}

export function formatPercent(v) {
  if (v === null || v === undefined) return '—';
  return `${v}%`;
}

export function effectivityThresholdColor(pct, colors) {
  if (pct === null || pct === undefined) return colors.info;
  if (pct >= 70) return colors.success;
  if (pct >= 40) return colors.warning;
  return colors.error;
}

export function targetThresholdColor(pct, colors) {
  if (pct === null || pct === undefined) return colors.info;
  if (pct >= 95) return colors.success;
  if (pct >= 80) return colors.warning;
  return colors.error;
}

export function packageProgressColor(pct, colors) {
  if (pct === null || pct === undefined) return colors.info;
  if (pct > 100) return colors.error;
  if (pct > 80) return colors.warning;
  return colors.success;
}

/**
 * Klemmt einen Datumsbereich so, dass weder startDate noch endDate nach heute liegt.
 * Lexikografischer Vergleich auf 'YYYY-MM-DD' ist chronologisch korrekt.
 */
export function clampDateRange({ startDate, endDate }) {
  const today = moment().format('YYYY-MM-DD');
  return {
    startDate: startDate && startDate > today ? today : startDate,
    endDate: endDate && endDate > today ? today : endDate,
  };
}

/** Klemmt ein End-Moment auf heute, falls es in der Zukunft liegt. */
function clampEnd(endMoment) {
  const now = moment();
  return endMoment.isAfter(now, 'day') ? now : endMoment;
}

export function previousPeriod(startDate, endDate) {
  const start = moment(startDate);
  const end = moment(endDate);
  const lengthDays = end.diff(start, 'days') + 1;
  return {
    startDate: start.clone().subtract(lengthDays, 'days').format('YYYY-MM-DD'),
    endDate: start.clone().subtract(1, 'days').format('YYYY-MM-DD'),
  };
}

export function workedDelta(current, previous) {
  if (!previous) return { type: 'new', deltaSeconds: 0 };
  const c = current?.workedTimeInSeconds || 0;
  const p = previous?.workedTimeInSeconds || 0;
  const delta = c - p;
  return { type: delta >= 0 ? 'up' : 'down', deltaSeconds: delta };
}

export function teamSummary(entries) {
  if (!entries || entries.length === 0) {
    return {
      teamWorkedSeconds: 0,
      avgEffectivity: null,
      avgTargetAchievement: null,
      avgAbsenceRate: null,
      activeEmployees: 0,
      bookingDaysTotal: 0,
      teamBillableSeconds: 0,
    };
  }

  const teamWorkedSeconds = entries.reduce(
    (acc, e) => acc + (e.workedTimeInSeconds || 0),
    0,
  );
  const teamBillableSeconds = entries.reduce(
    (acc, e) => acc + (e.billableWorkedTimeInSeconds || 0),
    0,
  );
  const bookingDaysTotal = entries.reduce(
    (acc, e) => acc + (e.bookingDaysCount || 0),
    0,
  );

  const effectivityValues = entries
    .map((e) => effectivityPercent(e))
    .filter((v) => v !== null);
  const targetValues = entries
    .map((e) => targetAchievementPercent(e))
    .filter((v) => v !== null);
  const absenceValues = entries
    .map((e) => absenceRatePercent(e))
    .filter((v) => v !== null);

  const avg = (arr) =>
    arr.length === 0
      ? null
      : Math.round(arr.reduce((a, b) => a + b, 0) / arr.length);

  return {
    teamWorkedSeconds,
    teamBillableSeconds,
    avgEffectivity: avg(effectivityValues),
    avgTargetAchievement: avg(targetValues),
    avgAbsenceRate: avg(absenceValues),
    activeEmployees: entries.length,
    bookingDaysTotal,
  };
}

export const SORT_OPTIONS = [
  { key: 'customer', labelKey: 'Customer hours', dir: 'desc' },
  { key: 'name', labelKey: 'Name', dir: 'asc' },
  { key: 'target', labelKey: 'Target achievement', dir: 'desc' },
  { key: 'effectivity', labelKey: 'Effectivity', dir: 'desc' },
  { key: 'absence', labelKey: 'Absence rate', dir: 'asc' },
  { key: 'bookingDays', labelKey: 'Booking days', dir: 'desc' },
];

export const DATE_PRESETS = [
  {
    key: 'today',
    labelKey: 'Today',
    get: () => ({
      start: moment().startOf('day'),
      end: clampEnd(moment().endOf('day')),
    }),
  },
  {
    key: 'thisWeek',
    labelKey: 'This week',
    get: () => ({
      start: moment().startOf('isoWeek'),
      end: clampEnd(moment().endOf('isoWeek')),
    }),
  },
  {
    key: 'thisMonth',
    labelKey: 'This month',
    get: () => ({
      start: moment().startOf('month'),
      end: clampEnd(moment().endOf('month')),
    }),
  },
  {
    key: 'thisQuarter',
    labelKey: 'This quarter',
    get: () => ({
      start: moment().startOf('quarter'),
      end: clampEnd(moment().endOf('quarter')),
    }),
  },
  {
    key: 'thisYear',
    labelKey: 'This year',
    get: () => ({
      start: moment().startOf('year'),
      end: clampEnd(moment().endOf('year')),
    }),
  },
  {
    key: 'lastMonth',
    labelKey: 'Last month',
    get: () => ({
      start: moment().subtract(1, 'month').startOf('month'),
      end: moment().subtract(1, 'month').endOf('month'),
    }),
  },
  {
    key: 'lastQuarter',
    labelKey: 'Last quarter',
    get: () => ({
      start: moment().subtract(1, 'quarter').startOf('quarter'),
      end: moment().subtract(1, 'quarter').endOf('quarter'),
    }),
  },
  {
    key: 'lastYear',
    labelKey: 'Last year',
    get: () => ({
      start: moment().subtract(1, 'year').startOf('year'),
      end: moment().subtract(1, 'year').endOf('year'),
    }),
  },
];

export function matchDatePreset(startDate, endDate) {
  for (const preset of DATE_PRESETS) {
    const { start, end } = preset.get();
    if (
      start.format('YYYY-MM-DD') === startDate &&
      end.format('YYYY-MM-DD') === endDate
    ) {
      return preset;
    }
  }
  return null;
}

export function sortEntries(entries, sortKey) {
  const list = [...entries];
  switch (sortKey) {
    case 'name':
      return list.sort((a, b) => {
        const an = `${a.user.firstName || ''} ${a.user.lastName || ''}`
          .trim()
          .toLowerCase();
        const bn = `${b.user.firstName || ''} ${b.user.lastName || ''}`
          .trim()
          .toLowerCase();
        return an.localeCompare(bn);
      });
    case 'target':
      return list.sort(
        (a, b) =>
          (targetAchievementPercent(b) ?? -1) -
          (targetAchievementPercent(a) ?? -1),
      );
    case 'effectivity':
      return list.sort(
        (a, b) => (effectivityPercent(b) ?? -1) - (effectivityPercent(a) ?? -1),
      );
    case 'absence':
      return list.sort(
        (a, b) =>
          (absenceRatePercent(a) ?? Infinity) -
          (absenceRatePercent(b) ?? Infinity),
      );
    case 'bookingDays':
      return list.sort(
        (a, b) => (b.bookingDaysCount || 0) - (a.bookingDaysCount || 0),
      );
    case 'customer':
    default:
      return list.sort(
        (a, b) =>
          (b.customerWorkedTimeInSeconds || 0) -
          (a.customerWorkedTimeInSeconds || 0),
      );
  }
}

export function filterEntries(entries, search) {
  const trimmed = (search || '').trim().toLowerCase();
  if (!trimmed) return entries;
  return entries.filter((e) => {
    const fullName = `${e.user.firstName || ''} ${e.user.lastName || ''}`
      .trim()
      .toLowerCase();
    return fullName.includes(trimmed);
  });
}
